import { api } from './client';

export const HANDOFF_DEVICE_STORAGE_KEY = 'khiimori_handoff_device_id';

export interface HandoffDeviceCapability {
  can_receive_audio?: boolean;
  can_receive_video?: boolean;
  screen_size?: string;
}

export interface HandoffDeviceDTO {
  id: string;
  name: string;
  type: string;
  platform?: string;
  last_seen: string;
  is_online: boolean;
  capability?: HandoffDeviceCapability;
}

export interface TabSummaryDTO {
  url?: string;
  title?: string;
}

export interface HandoffTabDTO {
  url?: string;
  title?: string;
  favicon?: string;
  open_tabs?: TabSummaryDTO[];
}

export interface HandoffSessionDTO {
  id: string;
  source_device: string;
  target_device: string;
  tab?: HandoffTabDTO | null;
  created_at: string;
  expires_at: string;
  status: string;
}

export function getOrCreateHandoffDeviceId(): string {
  let id = localStorage.getItem(HANDOFF_DEVICE_STORAGE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(HANDOFF_DEVICE_STORAGE_KEY, id);
  }
  return id;
}

export async function registerHandoffDevice(body: {
  id?: string;
  name: string;
  type?: string;
  platform?: string;
  capability?: HandoffDeviceCapability;
}): Promise<HandoffDeviceDTO> {
  const { data } = await api.post<HandoffDeviceDTO>('/handoff/devices', body);
  return data;
}

export async function pingHandoffDevice(deviceId: string): Promise<void> {
  await api.put(`/handoff/devices/${encodeURIComponent(deviceId)}/ping`);
}

export async function listHandoffDevices(): Promise<HandoffDeviceDTO[]> {
  const { data } = await api.get<{ devices: HandoffDeviceDTO[] }>('/handoff/devices');
  return data.devices ?? [];
}

export async function listHandoffSessions(limit = 50): Promise<HandoffSessionDTO[]> {
  const { data } = await api.get<{ sessions: HandoffSessionDTO[] }>('/handoff/sessions', {
    params: { limit },
  });
  return data.sessions ?? [];
}

export async function sendHandoff(payload: {
  source_device_id: string;
  target_device_id: string;
  tab_id?: string;
  open_tabs?: TabSummaryDTO[];
}): Promise<{ session_id: string; status: string; source_device: string; target_device: string }> {
  const { data } = await api.post('/handoff/send', payload);
  return data;
}

export async function listIncomingHandoffs(deviceId: string): Promise<HandoffSessionDTO[]> {
  const { data } = await api.get<{ handoffs: HandoffSessionDTO[] }>('/handoff/incoming', {
    params: { device_id: deviceId },
  });
  return data.handoffs ?? [];
}

export async function receiveHandoff(handoffId: string): Promise<{ handoff_id: string; status: string }> {
  const { data } = await api.post(`/handoff/receive/${encodeURIComponent(handoffId)}`);
  return data;
}
